import utils from '@/utils';
import { clone, buildApiQuery } from '@/utils/dataUtil';
import { BILLING_REF_API } from '@/api';
import { getMomentDateString } from '@/utils/dateUtil';

const state = {
  tableKey: -999,
  currentTableRow: null,
  tableData: [],
  contractList: [],
  chargeCodeList: [],
  nullTableRow: {
    id: -999,
    bdName: null,
    contractId: null,
    contractName: null,
    chargeCodeId: null,
    chargeCodeName: null,
    startDate: null,
    endDate: null,
    order: null,
    glAccountCode: null,
    glDescription: null,
    fercAccount: null,
    fercClass: null,
    profitCenter: null,
    costCenter: null,
    orderNumber: null,
    createdBy: null,
    createdDate: null,
    updatedBy: null,
    updatedDate: null,
  },
};

const getters = {
  getContractList: (state) => state.contractList.map(({ id, name }) => ({ value: id, label: name })),
  getChargeList: (state) => state.chargeCodeList.map(({ id, chargeCodeName }) => ({ value: id, label: chargeCodeName })),
};

const actions = {
  initialize({ commit, dispatch, state }) {
    dispatch('fetchContractList');
    dispatch('fetchChargeCodeList');
  },
  async fetchContractChargeRef({ commit }, parameters = {}) {
    const params = {
      contracts: parameters.contractsSelected,
      chargeCodes: parameters.chargeCodesSelected.join(','),
    };
    try {
      const { data: { data } } = await BILLING_REF_API.post('/contract-charge-ref/get', params);
      if (Array.isArray(data)) {
        data.forEach((obj) => {
          obj.startDate = getMomentDateString(obj.startDate);
          obj.endDate = obj.endDate === null ? null : getMomentDateString(obj.endDate);
        });
        commit('setTableData', data);
      }
    } catch (err) {
      console.error(err);
      this.$notify('Failed to load Contract Charge Ref Data', 'error');
    }
  },
  fetchContractList({ commit }) {
    BILLING_REF_API.get('/contracts').then(({ data }) => {
      commit('loadContractList', data.data);
    }).catch((err) => {
      console.error(err);
    });
  },
  fetchChargeCodeList({ commit }) {
    BILLING_REF_API.get('/charge-codes').then(({ data }) => {
      commit('loadChargeCodeList', data.data);
    }).catch((err) => {
      console.error(err);
    });
  },
  postRecord({ state }, record) {
    record.startDate = getMomentDateString(record.startDate);
    record.endDate = record.endDate === null ? null : getMomentDateString(record.endDate);

    BILLING_REF_API.post('/contract-charge-ref', record).then(({ data }) => {
      data.startDate = getMomentDateString(data.startDate);
      data.endDate = data.endDate === null ? null : getMomentDateString(data.endDate);

      state.tableData.push(data);
      this.$notify('Data Added', 'success');
    }).catch((err) => {
      if (err.response.data === 'Reference data already exists') {
        this.$notify('Failed to Add Data. Data already exists', 'error');
      } else {
        this.$notify('Failed to Add Data', 'error');
      }
    });
  },
  updateRecord({ dispatch, commit, state }, record) {
    record.startDate = getMomentDateString(record.startDate);
    record.endDate = record.endDate === null ? null : getMomentDateString(record.endDate);

    BILLING_REF_API.put(`/contract-charge-ref/${record.id}`, record).then(({ data }) => {
      data.startDate = getMomentDateString(data.startDate);
      data.endDate = data.endDate === null ? null : getMomentDateString(data.endDate);

      commit('updateRecord', data);
      this.$notify('Data Updated', 'success');
    }).catch((err) => {
      this.$notify('Failed to Update Data', 'error');
    });
  },
  deleteTableRow({ dispatch, commit, state }) {
    BILLING_REF_API.delete(`/contract-charge-ref/${state.currentTableRow.id}`).then(({ data }) => {
      commit('deleteTableRow');
      dispatch('currentTableRowChange', clone(state.nullTableRow));
      this.$notify('Data Removed', 'success');
    }).catch((err) => {
      this.$notify('Failed to Remove Data', 'error');
    });
  },
};

const mutations = {
  setTableData(state, value) {
    state.tableData = value;
  },
  updateRecord(state, record) {
    const rowIndex = state.tableData.findIndex(({ id }) => id === record.id);
    state.tableData.splice(rowIndex, 1, record);
  },
  currentTableRowChange(state, currentRow) {
    state.currentTableRow = currentRow;
  },
  reset(state) {
    state.tableData = [];
  },
  loadContractList(state, contracts) {
    state.contractList = contracts;
  },
  loadChargeCodeList(state, chargeCodes) {
    state.chargeCodeList = chargeCodes;
  },
  createTableRow(state) {
    const record = clone(state.nullTableRow);
    record.id = state.tableKey++;
    record.versionId = 1;
    record.createdBy = this.getters['auth/getProfile'].userName;
    record.createdDate = utils.date.getNow().utc().format();
    state.currentTableRow = record;
  },
  copyTableRow(state) {
    const record = clone(state.currentTableRow);
    record.id = state.tableKey++;
    record.versionId = 1;
    record.createdBy = this.getters['auth/getProfile'].userName;
    record.createdDate = utils.date.getNow().utc().format();
    state.currentTableRow = record;
  },
  deleteTableRow(state) {
    state.tableData = state.tableData.filter((record) => record.id !== state.currentTableRow.id);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};